/**
 * PROLINE-3621 — the API returns `date` (`YYYY-MM-DD`) and `time` (`HH:MM:SS`)
 * ready to display, so rows carry them through without date parsing.
 */

/** List query params. `name` search is not in the contract yet — see CONTEXT note. */
export const buildClientReportsListParams = ({ page, pageSize, search }) => {
  const params = { page, items: pageSize, order_by_created_at: "desc" };
  const term = (search || "").trim();
  if (term) params.name = term;
  return params;
};

/** `page=-1` returns every report unpaginated — used by Download All. */
export const buildClientReportsAllParams = () => ({
  page: -1,
  order_by_created_at: "desc",
});

/** `2026-08-24` → `24-08-2026` (display order). */
export const formatClientReportDate = (date) => {
  const parts = String(date || "").split("-");
  if (parts.length !== 3) return date || "";
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

/** `3.2 MB` / `640 KB` — the size column and the modal file rows. */
export const formatClientReportSize = (bytes) => {
  const size = Number(bytes);
  if (!size || Number.isNaN(size)) return "";
  const kb = size / 1024;
  if (kb >= 1024) return `${(kb / 1024).toFixed(1)} MB`;
  return `${Math.round(kb)} KB`;
};

const FILE_TYPE_BY_EXTENSION = {
  pdf: "pdf",
  png: "png",
  jpg: "jpg",
  jpeg: "jpg",
  xls: "xlsx",
  xlsx: "xlsx",
  html: "html",
  htm: "html",
};

/** Canonical `pdf` / `png` / `jpg` / `xlsx` / `html` key from the content type or the file name. */
export const resolveClientReportFileType = (contentType, fileName) => {
  const type = String(contentType || "").toLowerCase();
  if (type.includes("pdf")) return "pdf";
  if (type.includes("png")) return "png";
  if (type.includes("jpeg") || type.includes("jpg")) return "jpg";
  if (type.includes("spreadsheet") || type.includes("excel")) return "xlsx";
  if (type.includes("html")) return "html";

  const ext = String(fileName || "").split(".").pop()?.toLowerCase();
  return FILE_TYPE_BY_EXTENSION[ext] || "";
};

/** `PDF document` / `PNG image` — the file-name cell subtitle. */
export const getClientReportFileTypeLabel = (fileType, labels = {}) =>
  labels[fileType] || "";

export const mapClientReportRow = (report) => ({
  id: report?.id,
  name: report?.name || "-",
  size: Number(report?.size) || 0,
  fileType: resolveClientReportFileType(report?.content_type, report?.name),
  // `creator` is null for Admin-Portal uploads and for pre-tracking reports.
  uploaderName: report?.creator?.name || "",
  uploaderType: report?.creator?.type || "",
  date: report?.date || "",
  time: report?.time || "",
  uploadUrl: report?.upload_url || "",
  // Server-evaluated per caller — drives the Delete control, never re-derived
  // from `creator.type` client-side (PROLINE-3636).
  isDeletable: Boolean(report?.is_deletable),
  raw: report,
});
